import React, { createContext, useContext, useEffect, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../stores/authStore";
import { authAPI } from "../services/authAPI";
import { setNavigateFunction } from "../utils/navigation";

interface AuthContextType {
  login: (
    email: string,
    password: string,
    totpCode?: string
  ) => Promise<{ requires2fa?: boolean }>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  React.useEffect(() => {
    setNavigateFunction(navigate);
  }, [navigate]);

  const storeLogin = useAuthStore((s) => s.login);
  const storeLogout = useAuthStore((s) => s.logout);
  const setHasHydrated = useAuthStore((s) => s.setHasHydrated);
  const hasHydrated = useAuthStore((s) => s._hasHydrated);
  const [isLoading, setIsLoading] = React.useState(true);
  const loggingOutRef = React.useRef(false);

  const clearAuthStorage = () => {
    try {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("paytool-auth-v2");
      localStorage.removeItem("auth-storage");
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (useAuthStore.persist.hasHydrated()) {
      setHasHydrated(true);
      return;
    }
    const unsub = useAuthStore.persist.onFinishHydration(() => {
      setHasHydrated(true);
    });
    return unsub;
  }, [setHasHydrated]);

  useEffect(() => {
    // Persist rehydrate must finish first, otherwise it can overwrite /me permissions.
    if (!hasHydrated) return;

    const publicPaths = new Set([
      "/",
      "/login",
      "/register",
      "/privacy-policy",
    ]);

    const verifyToken = async () => {
      if (loggingOutRef.current) {
        setIsLoading(false);
        return;
      }

      const token =
        useAuthStore.getState().accessToken ||
        localStorage.getItem("accessToken");
      const path = window.location.pathname;

      if (token) {
        try {
          const userData = await authAPI.getMe();
          if (loggingOutRef.current) {
            setIsLoading(false);
            return;
          }
          const refreshToken =
            useAuthStore.getState().refreshToken ||
            localStorage.getItem("refreshToken") ||
            "";
          storeLogin(userData, token, refreshToken);
          localStorage.setItem("accessToken", token);
          if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
        } catch {
          clearAuthStorage();
          storeLogout();
          if (!publicPaths.has(path)) {
            navigate("/login", { replace: true });
          }
        }
      } else {
        storeLogout();
        if (!publicPaths.has(path)) {
          navigate("/login", { replace: true });
        }
      }

      setIsLoading(false);
    };

    verifyToken();
  }, [hasHydrated, navigate, storeLogin, storeLogout]);

  const login = async (email: string, password: string, totpCode?: string) => {
    loggingOutRef.current = false;
    const response = await authAPI.login({ email, password, totpCode });
    if ("requires2fa" in response && response.requires2fa) {
      return { requires2fa: true };
    }
    if (!("accessToken" in response) || !response.accessToken) {
      throw new Error("Login failed: no access token");
    }

    const { user, accessToken, refreshToken } = response;

    localStorage.setItem("accessToken", accessToken);
    if (refreshToken) {
      localStorage.setItem("refreshToken", refreshToken);
    }
    queryClient.clear();
    storeLogin(user, accessToken, refreshToken);
    return {};
  };

  const logout = () => {
    // Clear tokens sync first so /login does not bounce back to dashboard
    // via leftover accessToken + verifyToken/getMe.
    loggingOutRef.current = true;
    clearAuthStorage();
    storeLogout();
    queryClient.clear();
    void authAPI.logout().catch(() => undefined);
    navigate("/login", { replace: true });
  };

  const refreshUser = async () => {
    if (loggingOutRef.current) return;
    const token =
      useAuthStore.getState().accessToken ||
      localStorage.getItem("accessToken");
    if (token) {
      try {
        const userData = await authAPI.getMe();
        const refreshToken =
          useAuthStore.getState().refreshToken ||
          localStorage.getItem("refreshToken") ||
          "";
        storeLogin(userData, token, refreshToken);
      } catch {
        logout();
      }
    }
  };

  return (
    <AuthContext.Provider value={{ login, logout, refreshUser, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};
